/* eslint-disable max-lines-per-function */
import { noop } from 'lodash';
import { useMemo } from 'react';

import {
   EmergencyNotificationHistory,
   EmergencyNotificationHistoryQuery,
} from '@dreryk/egabinet-emergency-notification-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { createDynamicColumnsWidth, StyledFlex, TableColumnConfiguration, Text } from '@dreryk/react-components';
import { dateTimeFormatter, useTranslate } from '@dreryk/react-common';

import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';

import { translationKeys } from '../../translations';

import { AcknowledgedByCell } from './cells/AcknowledgedByCell';

export const useEmergencyNotificationHistoryColumns = (): TableColumnConfiguration<EmergencyNotificationHistory>[] => {
   const translate = useTranslate();

   return useMemo((): TableColumnConfiguration<EmergencyNotificationHistory>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         triggeredAt: '160px',
         reporter: '100%',
         notificationChannel: '180px',
         status: '110px',
         acknowledgedBy: '310px',
      });

      return [
         {
            name: 'triggeredAt',
            accessor: noop,
            label: translationKeys.dateAndTime,
            width: columnsWidth.triggeredAt,
            sortingDisabled: true,
            renderer: ({ bean }) => {
               return (
                  <StyledFlex direction={'column'}>
                     <Text testId={'triggeredAt'}>{dateTimeFormatter(bean.triggeredAt)}</Text>
                  </StyledFlex>
               );
            },
         },
         {
            name: 'reporter',
            accessor: noop,
            label: translationKeys.reportedFrom,
            width: columnsWidth.reporter,
            sortingDisabled: true,
            renderer: ({ bean }) => {
               return (
                  <PractitionerSafePresenter
                     testId={'author'}
                     practitioner={bean.reporter}
                     type={'standalone'}
                     withIdentification
                  />
               );
            },
         },
         {
            name: 'notificationChannel',
            accessor: noop,
            label: translationKeys.notificationChannel,
            width: columnsWidth.notificationChannel,
            sortingDisabled: true,
            renderer: ({ bean }) => {
               return (
                  <Text testId={'notificationChannel'}>
                     {bean.channels
                        .map((channel) => translate(translationKeys.notificationChannelsMap[channel]))
                        .join(', ')}
                  </Text>
               );
            },
         },
         {
            name: 'status',
            accessor: noop,
            label: translationKeys.status,
            width: columnsWidth.status,
            sortingDisabled: true,
            renderer: ({ bean }) => {
               return <Text testId={'status'}>{translate(translationKeys.statusesMap[bean.status])}</Text>;
            },
         },
         {
            name: 'acknowledgedBy',
            accessor: noop,
            label: translationKeys.acknowledgedBy,
            width: columnsWidth.acknowledgedBy,
            sortingDisabled: true,
            renderer: ({ bean }) => {
               return <AcknowledgedByCell acknowledgments={bean.acknowledgments} />;
            },
         },
      ];
   }, [translate]);
};

export const useEmergencyNotificationHistoryDataConfiguration = (): LazyLoadedDataConfigurationByCqrsQuery<EmergencyNotificationHistory> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<EmergencyNotificationHistory>(() => {
      return new EmergencyNotificationHistoryQuery();
   });
};
